import { Fragment } from 'react'

import { StyleSheet, View } from '../../compiler'
import { colors, typoStyles } from '../../constants'
import MarkdownText from '../MarkdownText'
import DimensionTitle from './DimensionTitle'
import { pad2, selectTopDimensions } from './rank'

/**
 * Hervorhebung der drei wichtigsten Werte als Bande über der Rangliste.
 * Portiert aus aivy-pdf-reports (src/reports_v2/components/culture/
 * CultureTopThree.js), Auswahl und Farben unverändert.
 *
 * Im Web stehen die drei Werte untereinander statt in drei Spalten. Die Karte
 * ist in Discover 592 bis 816px breit, eine Spalte hätte damit 150 bis 220px
 * und Titel wie "Dynamik & Herausforderung" würden abgeschnitten. Gestapelt
 * fluchten Badge und Titel außerdem mit den Zeilen der Rangliste darunter.
 * Die festen Höhen und maxLines aus dem PDF sind Seitenbudget für
 * wrap={false} und entfallen, es gibt keine Paginierung.
 *
 * withBottomBorder={false} setzt die Rangliste, wenn unter der Bande nichts
 * mehr folgt. Sonst läge die Trennlinie doppelt auf dem Kartenrand.
 *
 * Payload: culture_section.top_dimensions
 *   { title, hint, data: [{ rank, key, title, bullets: [{ label, text }] }] }
 * Labels kommen fertig aus dem Backend, hier wird nichts gemappt.
 */

const BADGE = 40
const PAD = 24

/* Das PDF begrenzt auf drei Zeilen (Fokus, Wirkung, Ausdruck). */
const MAX_LINES = 3

/* Alle drei Werte teilen sich ein Grid aus Badge, Label und Text. So fluchten
   die Labels auch dann, wenn ein Wert weniger bullets hat. Die Badge-Spalte
   ist 8px breiter als das Badge, mit dem Spaltenabstand von 16px steht der
   Titel damit wie in den Zeilen der Rangliste 24px neben dem Badge. */
const COLUMN_GAP = 16
const GRID_COLUMNS = `${BADGE + PAD - COLUMN_GAP}px max-content minmax(0, 1fr)`

const styles = StyleSheet.create({
  band: {
    paddingHorizontal: PAD,
    paddingVertical: PAD,
    backgroundColor: colors.paleWhiteBlue,
    borderBottomWidth: 1,
    borderColor: colors.lightGrey
  },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 16,
    marginBottom: 16
  },
  headerTitle: { ...typoStyles.T2MediumBlue, textTransform: 'uppercase' },
  headerHint: { ...typoStyles.SmallBlack, color: colors.rockGrey },

  values: {
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    columnGap: COLUMN_GAP,
    rowGap: 8,
    alignItems: 'start'
  },

  /* lightGrey (#EBF0F5) wäre auf paleWhiteBlue (#EBEFFD) unsichtbar, deshalb
     wie im PDF lightBlue. Im Web horizontal, weil die Werte gestapelt sind. */
  rule: {
    gridColumn: '1 / -1',
    height: 1,
    backgroundColor: colors.lightBlue,
    marginVertical: 12
  },

  /* gefüllt statt Ring wie in der Rangliste, der Block ist die Hervorhebung */
  badge: {
    gridColumn: '1',
    width: BADGE,
    height: BADGE,
    borderRadius: BADGE / 2,
    backgroundColor: colors.primaryBlue,
    alignItems: 'center',
    justifyContent: 'center'
  },
  titleCell: {
    gridColumn: '2 / -1',
    minHeight: BADGE,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center'
  },
  valueTitle: typoStyles.H4Black,

  lineLabel: {
    ...typoStyles.SmallBlue,
    gridColumn: '2',
    alignSelf: 'baseline',
    textTransform: 'uppercase',
    display: 'block'
  },
  lineText: {
    ...typoStyles.T2Black,
    gridColumn: '3',
    alignSelf: 'baseline',
    display: 'block'
  }
})

/* Explizite Zeilen statt Auto-Placement, weil das Badge über Titel und alle
   bullets seines Werts reicht. */
const renderValue = ({
  item,
  row,
  onClickDimension,
  hasCompletedOnboarding
}) => {
  const bullets = item.bullets.slice(0, MAX_LINES)

  return [
    <View
      key='badge'
      style={[styles.badge, { gridRow: `${row} / span ${bullets.length + 1}` }]}
    >
      <MarkdownText style={typoStyles.H4White}>{pad2(item.rank)}</MarkdownText>
    </View>,
    <View key='title' style={[styles.titleCell, { gridRow: `${row}` }]}>
      <DimensionTitle
        title={item.title}
        style={styles.valueTitle}
        onClick={onClickDimension ? () => onClickDimension(item) : undefined}
        hasCompletedOnboarding={hasCompletedOnboarding}
      />
    </View>,
    ...bullets.flatMap((bullet, index) => [
      <MarkdownText
        key={`label-${index}`}
        style={{ ...styles.lineLabel, gridRow: `${row + 1 + index}` }}
      >
        {bullet.label}
      </MarkdownText>,
      <MarkdownText
        key={`text-${index}`}
        style={{ ...styles.lineText, gridRow: `${row + 1 + index}` }}
      >
        {bullet.text}
      </MarkdownText>
    ])
  ]
}

const CultureTopThree = ({
  title,
  hint,
  data = [],
  withHeader = true,
  withBottomBorder = true,
  onClickDimension,
  hasCompletedOnboarding = true
}) => {
  const dimensions = selectTopDimensions(data)

  if (!dimensions.length) return null

  /* Startzeile je Wert. Ein Wert belegt Titel plus bullets, danach folgt eine
     Zeile für die Trennlinie. */
  let nextRow = 1
  const rows = dimensions.map(({ bullets }) => {
    const row = nextRow
    nextRow += 1 + Math.min(MAX_LINES, bullets.length) + 1
    return row
  })

  return (
    <View style={[styles.band, !withBottomBorder && { borderBottomWidth: 0 }]}>
      {withHeader && (
        <View style={styles.headerRow}>
          <MarkdownText style={styles.headerTitle}>{title}</MarkdownText>
          {!!hint && (
            <MarkdownText style={styles.headerHint}>{hint}</MarkdownText>
          )}
        </View>
      )}

      <View style={styles.values}>
        {dimensions.map((item, index) => {
          const row = rows[index]

          return (
            <Fragment key={item.key || index}>
              {index > 0 && (
                <View style={[styles.rule, { gridRow: `${row - 1}` }]} />
              )}
              {renderValue({
                item,
                row,
                onClickDimension,
                hasCompletedOnboarding
              })}
            </Fragment>
          )
        })}
      </View>
    </View>
  )
}

export default CultureTopThree
